import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Loader, PlayCircle } from "lucide-react";
import TotalGapSummary from "./TotalGapSummary";
import ConsumerHouseReconciliation from "./ConsumerHouseReconciliation";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import { useConsumerHouseReconciliation, useDailyPosition, useRunPositionCheck } from "../hooks/useTreasury";
import CurrencyGapPanel from "./CurrencyGapPanel";
import ManualReviewSection from "./ManualReviewSection";
import LiabilityCards from "./LiabilityCards";
import { investigateItems } from "../utils/reconciliationNotes";

const formatAmount = (value) => {
  const num = Number(value);
  return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
};

// Consumer wallets vs. the house-merchant "pass-through" wallet a
// consumer's deposit was actually relayed through before crediting their
// own wallet (identified by SEPTLY_MERCHANT_ID - NOT all merchant accounts
// on Novacrust, just that one collection business). Unlike the Merchant ↔
// Partner page, this isn't reconciling against an external partner - it's
// asking whether that specific internal collection wallet still covers
// what consumers currently hold. Note it only ever grows (nothing debits
// it once a deposit relays onward), so "covered" today doesn't guarantee
// it stays covered.
function ConsumerMerchantPositionTable() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { data, isLoading, isError, error } = useDailyPosition(date);
  // Same underlying job as Merchant ↔ Partner's button - computeDailyPosition
  // recomputes every level (merchant/consumer/pass_through/combined) in one
  // run, there's no separate "consumer-only" job to trigger.
  const { mutate: runCheck, isPending: isRunning } = useRunPositionCheck();

  // Currencies whose gap explanation is open. The explanation (and the records
  // behind each cause) is the live reconciliation, fetched the first time a
  // currency is opened and then reused. It is all-time, so it is comparable
  // to the live gap.
  const [opened, setOpened] = useState(() => new Set());
  const toggleCurrency = (currency) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(currency)) next.delete(currency);
      else next.add(currency);
      return next;
    });
  const {
    data: recon,
    isLoading: reconLoading,
    isError: reconError,
    refetch: refetchRecon,
  } = useConsumerHouseReconciliation(undefined, { enabled: opened.size > 0 });
  const today = new Date().toISOString().slice(0, 10);

  const byCurrency = useMemo(() => {
    const grouped = new Map();
    for (const row of data ?? []) {
      if (row.level !== "consumer" && row.level !== "pass_through") continue;
      const entry = grouped.get(row.currency) ?? { currency: row.currency, levels: {} };
      entry.levels[row.level] = row;
      grouped.set(row.currency, entry);
    }
    return Array.from(grouped.values()).filter((c) => c.levels.pass_through);
  }, [data]);

  if (isError) {
    return (
      <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
        Error: {error.message}
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 justify-between items-start pb-4 md:flex-row md:items-center">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-4 py-2 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
        <div className="flex gap-3 items-center">
          <p className="text-xs text-gray-400 max-w-sm">
            Does the house-merchant wallet still cover what consumers currently
            hold? Payouts debit it as well as deposits crediting it. Not a check
            against partner funds - see Merchant ↔ Partner for that.
          </p>
          <button
            onClick={() => runCheck()}
            disabled={isRunning}
            className="flex flex-shrink-0 gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-[#013941] rounded-3xl transition-all hover:bg-[#012126] disabled:opacity-50"
          >
            <PlayCircle className={`w-4 h-4 ${isRunning ? "animate-spin" : ""}`} />
            Run position check now
          </button>
        </div>
      </div>

      <LiabilityCards date={date} show={["house", "consumer"]} />

      {!isLoading && byCurrency.length > 0 && (
        <TotalGapSummary
          rows={byCurrency.map((c) => c.levels.pass_through)}
          label="Total pass-through gap (USD), all currencies"
        />
      )}

      <ManualReviewSection />

      <ConsumerHouseReconciliation />

      {isLoading ? (
        <div className="flex h-[300px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading position <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="w-8 border-b" />
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Currency</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Consumer Owed</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Consumer Owed (USD)</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Pass-through (house merchant)</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Gap</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Gap (USD)</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {byCurrency.length > 0 ? (
                  byCurrency
                    .slice()
                    .sort((a, b) => Number(a.levels.pass_through.gapAmountUsd) - Number(b.levels.pass_through.gapAmountUsd))
                    .map(({ currency, levels }) => {
                      const passThrough = levels.pass_through;
                      const isShort = Number(passThrough.gapAmountUsd) < 0;
                      const isOpen = opened.has(currency);
                      const reconRow = recon?.rows?.find((r) => r.currency === currency);
                      return (
                        <React.Fragment key={currency}>
                        <tr
                          onClick={() => toggleCurrency(currency)}
                          aria-expanded={isOpen}
                          title="Click to see what explains this gap"
                          className="transition-colors cursor-pointer hover:bg-gray-50"
                        >
                          <td className="pl-3 text-gray-500">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm font-medium text-gray-900 md:px-6 md:py-5">{currency}</td>
                          <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                            {levels.consumer ? formatCurrencyAmount(levels.consumer.owedAmount, currency) : formatCurrencyAmount(passThrough.owedAmount, currency)}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                            {levels.consumer?.owedAmountUsd == null ? "-" : formatAmount(levels.consumer.owedAmountUsd)}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                            {formatCurrencyAmount(passThrough.heldAmount, currency)}
                          </td>
                          <td className={`px-4 py-3 font-sans text-sm text-right md:px-6 md:py-5 ${isShort ? "text-red-600" : "text-green-700"}`}>
                            {formatCurrencyAmount(passThrough.gapAmount, currency)}
                          </td>
                          <td className={`px-4 py-3 font-sans text-sm font-semibold text-right md:px-6 md:py-5 ${isShort ? "text-red-600" : "text-green-700"}`}>
                            {formatAmount(passThrough.gapAmountUsd)}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm md:px-6 md:py-5">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${isShort ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
                                }`}
                            >
                              {isShort ? "Shortfall" : "Covered"}
                            </span>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={8} className="p-0 border-t border-gray-100">
                              {reconLoading ? (
                                <p className="flex gap-2 items-center px-6 py-4 text-sm text-gray-500">
                                  <Loader className="w-4 h-4 animate-spin" /> Checking consumer and house-merchant movements...
                                </p>
                              ) : reconError || !recon ? (
                                <p className="px-6 py-4 text-sm text-red-600">
                                  Could not load the explanation.{" "}
                                  <button type="button" onClick={() => refetchRecon()} className="underline">
                                    Try again
                                  </button>
                                </p>
                              ) : !reconRow ? (
                                <p className="px-6 py-4 text-sm text-gray-500">
                                  No figures to explain for {currency}: neither consumers nor the house merchant hold any.
                                </p>
                              ) : (
                                <>
                                  {date !== today && (
                                    <p className="px-6 pt-3 text-xs text-gray-500">
                                      The row above is the position check for {date}. This explanation is live, as of now.
                                    </p>
                                  )}
                                  <CurrencyGapPanel
                                    row={reconRow}
                                    filtered={false}
                                    since={undefined}
                                    notes={investigateItems(recon.rows).filter((n) => n.currency === currency)}
                                  />
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      No position computed for this date yet - click "Run position check now".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsumerMerchantPositionTable;
